// Polyfills for ES features checked by Modernizr in older WebViews.
/* eslint-disable no-extend-native */

type PromiseWithResolvers<T> = {
    promise: Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: unknown) => void;
};

function polyfillPromiseWithResolvers(): void {
    if (typeof Promise.withResolvers === "function") return;
    Promise.withResolvers = function <T>(): PromiseWithResolvers<T> {
        let resolve!: (value: T | PromiseLike<T>) => void;
        let reject!: (reason?: unknown) => void;
        const promise = new Promise<T>((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return { promise, resolve, reject };
    };
}

function polyfillPromiseAllSettled(): void {
    if (typeof Promise.allSettled === "function") return;
    Promise.allSettled = function <T>(
        promises: Iterable<T | PromiseLike<T>>
    ): Promise<PromiseSettledResult<T>[]> {
        const mapped = Array.from(promises, (promise) =>
            Promise.resolve(promise).then(
                (value) => ({ status: "fulfilled", value }) as const,
                (reason) => ({ status: "rejected", reason }) as const
            )
        );
        return Promise.all(mapped);
    };
}

function polyfillPromiseFinally(): void {
    if (typeof Promise.prototype.finally === "function") return;
    Promise.prototype.finally = function <T>(
        this: Promise<T>,
        onFinally?: (() => void) | null
    ): Promise<T> {
        const handler =
            typeof onFinally === "function" ? onFinally : () => undefined;
        return this.then(
            (value) =>
                Promise.resolve(handler()).then(() => value as T),
            (reason) =>
                Promise.resolve(handler()).then(() => {
                    throw reason;
                })
        );
    };
}

function polyfillObjectFromEntries(): void {
    if (typeof Object.fromEntries === "function") return;
    Object.fromEntries = function <T>(
        entries: Iterable<readonly [PropertyKey, T]>
    ): Record<PropertyKey, T> {
        const result: Record<PropertyKey, T> = {};
        for (const [key, value] of entries) {
            result[key] = value;
        }
        return result;
    };
}

function polyfillRegExpDotAll(): void {
    if (!RegExp?.prototype) return;
    const desc = Object.getOwnPropertyDescriptor(RegExp.prototype, "dotAll");
    if (desc?.get) return;
    Object.defineProperty(RegExp.prototype, "dotAll", {
        configurable: true,
        get(this: RegExp): boolean {
            const flags = typeof this.flags === "string" ? this.flags : "";
            return flags.includes("s");
        },
    });
}

polyfillPromiseWithResolvers();
polyfillPromiseAllSettled();
polyfillPromiseFinally();
polyfillObjectFromEntries();
polyfillRegExpDotAll();

export {};
